import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { HQ_NAV } from "./hqNav";
import { ArrowRight, ClipboardCheck } from "lucide-react";

/* HQ Dashboard , stained-glass art tiles generated from the shared HQ_NAV
   config so the dashboard and slide-out menu always mirror each other.
   Square artwork on phones, tall panels on desktop (Aug 2 2026). */

const AUBERGINE  = "var(--hq-deep, #5A4565)";
const PLUM       = "var(--hq-accent, #6F4A7A)";
const PARCH_EDGE = "#EFE4CC";

export default function HQDashboard() {
  const { me, selfAuditEnabled } = useOutletContext() || {};
  const { t } = useLang();
  const name = me?.first_name || me?.name?.split(" ")[0] || "friend";

  return (
    <div data-testid="hq-dashboard" className="space-y-7" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>
          {t("hq.title")}
        </div>
        <h1 className="text-5xl mt-1.5 leading-tight" style={{ color: AUBERGINE, letterSpacing: "0.01em" }}>
          {t("hq.welcome")} <em style={{ color: PLUM }}>{name}</em>
        </h1>
        <p className="italic mt-2 text-base opacity-75 max-w-2xl">
          {t("hq.gladHere")}
        </p>
      </header>

      {selfAuditEnabled === true && <Link to="/account/self-audit" data-testid="hq-self-audit-announcement"
        className="group relative block overflow-hidden border-2 p-5 sm:p-6 bg-[#6E1E3A] text-white shadow-md hover:shadow-xl transition-[box-shadow,transform] hover:-translate-y-0.5"
        style={{ borderColor: "#C49247" }}>
        <div className="absolute right-0 top-0 h-full w-28 opacity-15" style={{ background: "repeating-linear-gradient(45deg, transparent 0 10px, #FFFFFF 10px 12px)" }} />
        <div className="relative flex items-center justify-between gap-4"><div className="flex items-start gap-4"><span className="w-11 h-11 shrink-0 bg-white/15 grid place-items-center border border-white/30"><ClipboardCheck className="w-6 h-6" /></span><div><div className="text-[10px] tracking-[0.35em] uppercase font-semibold text-[#F5D99A]">New · Customer Self Audit</div><h2 className="font-serif text-2xl sm:text-3xl mt-1">Check your orders or report one missing</h2><p className="text-sm text-white/80 mt-1">Flag an unpaid order you have paid, or send missing order details directly to Heritage.</p></div></div><ArrowRight className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition-transform" /></div>
      </Link>}

      <section>
        <div className="mb-3">
          <div className="text-[9px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>
            {t("hq.yourAccount")}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {HQ_NAV.filter((tile) => !tile.dashboardHidden).map((tile) => <Tile key={tile.slug} tile={tile} t={t} />)}
        </div>
      </section>
    </div>
  );
}

function Tile({ tile, t }) {
  if (tile.tileImage) {
    return (
      <a href={tile.href} target="_blank" rel="noopener noreferrer" data-testid={`hq-tile-${tile.slug}`}
        className="group relative block overflow-hidden rounded-md shadow-md hover:shadow-xl md:hover:-translate-y-1 transition-[box-shadow,transform] duration-200 aspect-square md:aspect-[3/4]"
        style={{ border: `1px solid ${PARCH_EDGE}` }}>
        <img src={tile.tileImage} alt="Bloom ID plant passport" className="absolute inset-0 w-full h-full object-cover object-center" />
        <span className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-center bg-[#FBF6E9]/94 border-t" style={{ borderColor: PARCH_EDGE }}>
          <tile.Icon className="w-5 h-5 mx-auto" style={{ color: PLUM }} />
          <span className="block mt-1 text-[10px] md:text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: AUBERGINE }}>{t(tile.titleKey)}</span>
          <span className="hidden md:block mt-1 text-[11px] italic leading-tight" style={{ color: PLUM }}>{t(tile.descKey)}</span>
        </span>
      </a>
    );
  }
  const Body = (
    <>
      {/* Square art on phones, tall stained-glass panel on desktop */}
      <img
        src={`/photos/hq/tiles/${tile.slug}-square.webp`}
        alt={t(tile.titleKey)}
        loading="lazy"
        className="hq-art-tile block md:hidden w-full h-auto"
      />
      <img
        src={`/photos/hq/tiles/${tile.slug}-tall.webp`}
        alt={t(tile.titleKey)}
        loading="lazy"
        className="hq-art-tile hidden md:block w-full h-auto"
      />
    </>
  );

  const className =
    "group block overflow-hidden rounded-md shadow-md hover:shadow-xl md:hover:-translate-y-1 transition-[box-shadow,transform] duration-200";
  const style = { border: `1px solid ${PARCH_EDGE}` };

  if (tile.external) {
    return (
      <a href={tile.href} target="_blank" rel="noopener noreferrer" data-testid={`hq-tile-${tile.slug}`} className={className} style={style}>
        {Body}
      </a>
    );
  }
  return (
    <Link to={tile.tileTo || tile.to} data-testid={`hq-tile-${tile.slug}`} className={className} style={style}>
      {Body}
    </Link>
  );
}
